<template>
  <b-card>
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Branding</h3>
      <p>You can customise various elements of the platform to create a familiar environment for your company</p>

      <template v-for="({ toggle, component, ...block }, key) in blocks">
        <toggle-block v-if="key === 'customiseDashboardCards'" :key="key" class="py-3 border-bottom"
          :title="block.title" :description="block.description">
            <component class="mt-3" :is="component" :segments="company.userSegments"
              :cards="company.dashboardCards" @submit="dashboardCards => updateCompany({ dashboardCards })"/>
        </toggle-block>
        <form-block v-else :key="key" class="py-3 border-bottom"
          :title="block.title" :description="block.description" :company="company"
          :err="formErrs[key]" :state="formStates[key]"
          :fields="block.fields" :value="getValues(key)" @input="values => saveValues(key, values)"/>
      </template>
    </template>
  </b-card>
</template>

<script>
import _get from 'lodash/get';
import gql from 'graphql-tag';
import FormBlock from '@/components/settings/FormBlock';
import ToggleBlock from '@/components/settings/ToggleBlock';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import AssetDetailsFragment from '@/graphql/fragments/AssetDetails.gql';
import { getFontList } from '@/lib/fonts';

const BLOCKS = {
  customiseBranding: {
    title: 'Logos & Fonts',
    description: 'Customise various fonts & icons for the platform',
    fields: [
      {
        key: 'branding.bigLogo',
        label: 'Big Logo',
        type: 'asset-image',
      },
      {
        key: 'branding.smallLogo',
        label: 'Small Logo',
        type: 'asset-image',
      },
      {
        key: 'branding.headerImage',
        label: 'Header Image',
        type: 'asset-image',
      },
      {
        key: 'branding.imDashboardHeaderImage',
        label: 'Internal Mobility Header Image',
        type: 'asset-image',
      },
      {
        key: 'branding.emailHeaderImage',
        label: 'Email Header Image',
        type: 'asset-image',
      },
      {
        key: 'branding.socialImage',
        label: 'Social Image',
        description: 'We recommend an image of 1200px × 628px (w × h)',
        type: 'asset-image',
      },
      {
        key: 'branding.loginImage',
        label: 'Login Page Image (optional)',
        type: 'asset-image',
      },
      {
        key: 'branding.registerImage',
        label: 'Register Page Image (optional)',
        type: 'asset-image',
      },
      {
        key: 'branding.headerFont',
        label: 'Header Font',
        type: 'select',
        options() {
          const { handle } = (this.$root ? this.$root.company : null) || {};
          return getFontList(handle).sort();
        },
      },
      {
        key: 'branding.bodyFont',
        label: 'Body Font',
        type: 'select',
        options() {
          const { handle } = (this.$root ? this.$root.company : null) || {};
          return getFontList(handle).sort();
        },
      },
      {
        key: 'branding.primaryColour',
        label: 'Primary Colour',
        type: 'colorPicker',
        defaultValue: '#0085FF',
      },
      {
        key: 'branding.secondaryColour',
        label: 'Secondary Colour',
        type: 'colorPicker',
        defaultValue: '#313743',
      },
      {
        key: 'branding.backgroundColour',
        label: 'Background Colour',
        type: 'colorPicker',
        defaultValue: '#FBFBFD',
      },
    ],
  },
  customiseDashboardCards: {
    title: 'Dashboard Cards',
    description: 'Customise dashboard cards on the dashboard.',
    component: () => import('@src/components/settings/DashboardCards'),
  },
  customiseSupportUrls: {
    title: 'Help & Policy Documents',
    description: 'Upload Help and/or Policy documents for your users',
    fields: [
      {
        key: 'branding.helpURL',
        label: 'Help Document',
        type: 'asset-document',
      },
      {
        key: 'branding.policyURL',
        label: 'Policy Document',
        type: 'asset-document',
      },
      {
        key: 'preferences.trainingVideoUrl',
        label: 'Training Video URL',
        type: 'string',
      },
    ],
  },
  customiseAuthPages: {
    title: 'Sign Up and Onboarding',
    description: 'Customise text on the login & register pages of the platform',
    fields: [
      {
        key: 'template.REGISTER_FORM_TITLE',
        label: 'Register Form Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_FORM_DESCRIPTION',
        label: 'Register Form Description',
        type: 'template',
      },
      {
        key: 'template.REGISTER_INFO_TITLE',
        label: 'Register Info Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_INFO_CONTENT',
        label: 'Register Info Content',
        type: 'template',
        description: 'Each new line will be a bullet point',
      },
      {
        key: 'template.LOGIN_FORM_TITLE',
        label: 'Login Form Title',
        type: 'template',
      },
      {
        key: 'template.LOGIN_FORM_DESCRIPTION',
        label: 'Login Form Description',
        type: 'template',
      },
      {
        key: 'template.REGISTER_NETWORK_PAGE_TITLE',
        label: 'Referrals Page Title',
        type: 'template',
      },
      {
        key: 'template.REGISTER_NETWORK_PAGE_DESCRIPTION',
        label: 'Referrals Page Description',
        type: 'template',
      },
      {
        key: 'branding.onboarding',
        label: 'Onboarding Steps Order',
        type: 'onboardingSteps',
      },
      {
        key: 'preferences.onboardingIntroVideoUrl',
        label: 'Onboarding Intro Video URL',
        type: 'string',
      },
    ],
  },
  customiseRecruiterAskForReferral: {
    title: 'Referral Request Template',
    description: 'Customise the Referral Request email sent from the platform to users',
    fields: [
      {
        key: 'template.EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_SUBJECT',
        label: 'Email Subject',
        multiline: true,
        type: 'template',
        tokens: {
          firstName: 'The user\'s first name',
          lastName: 'The user\'s last name',
          jobTitle: 'The job title',
        },
      },
      {
        key: 'template.EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_BODY',
        label: 'Email Message',
        multiline: true,
        type: 'template',
        tokens: {
          firstName: 'The user\'s first name',
          lastName: 'The user\'s last name',
          jobTitle: 'The job title',
        },
      },
    ],
  },
  advanced: {
    title: 'Advanced Settings',
    description: 'Advanced branding settings',
    fields: [
      {
        key: 'branding.customFooterHTML',
        label: 'Custom HTML to include in page footer on every page',
        type: 'multiline-string',
      },
      {
        key: 'branding.customCSS',
        label: 'Custom CSS',
        type: 'multiline-string',
      },
      {
        key: 'branding.customJS',
        label: 'Custom JS to include in HTML header',
        type: 'multiline-string',
      },
      {
        key: 'branding.disableCookieWarning',
        label: 'Disable the automatic Cookie warning? (e.g Non-EU or using a custom implementation)',
        type: 'boolean',
      },

    ],
  },
  // customiseRecruiterInviteEmployee: {
  //   title: 'Recruiter Invite User To Platform email',
  //   description: 'Customise the Invite-To-Platform email sent from the platform to users',
  //   fields: [
  //     {
  //       key: 'template.EMAIL_RECRUITER_INVITE_EMPLOYEE_TO_PLATFORM_SUBJECT',
  //       label: 'Email Subject',
  //       multiline: true,
  //       type: 'template',
  //       tokens: {
  //         firstName: 'The user\'s first name',
  //         lastName: 'The user\'s last name',
  //       },
  //     },
  //     {
  //       key: 'template.EMAIL_RECRUITER_INVITE_EMPLOYEE_TO_PLATFORM_BODY',
  //       label: 'Email Message',
  //       multiline: true,
  //       type: 'template',
  //       tokens: {
  //         firstName: 'The user\'s first name',
  //         lastName: 'The user\'s last name',
  //       },
  //     },
  //   ],
  // },
};

const CompanyBrandingSettingsFragment = gql`
  ${AssetDetailsFragment}

  fragment CompanyBrandingSettings on Company {
    id
    name
    userSegments: segments(where:{ users: true }) {
      id
      title
    }

    branding {
      bigLogo {
        ...AssetDetails
      }
      smallLogo {
        ...AssetDetails
      }
      loginImage {
        ...AssetDetails
      }
      registerImage {
        ...AssetDetails
      }
      headerImage {
        ...AssetDetails
      }
      imDashboardHeaderImage {
        ...AssetDetails
      }
      emailHeaderImage {
        ...AssetDetails
      }
      socialImage {
        ...AssetDetails
      }
      headerFont
      bodyFont
      primaryColour
      secondaryColour
      backgroundColour
      customCSS
      compiledCSS
      helpURL {
        ...AssetDetails
      }
      policyURL {
        ...AssetDetails
      }
      onboarding {
        name
      }

      customJS
      disableCookieWarning
    }

    dashboardCards {
      title
      body
      cta
      locale
      segments {
        id
        title
      }
    }

    preferences {
      onboardingIntroVideoUrl
      trainingVideoUrl
    }

    templates(where: {
      keys: [
        "REGISTER_FORM_TITLE",
        "REGISTER_FORM_DESCRIPTION",
        "REGISTER_INFO_TITLE",
        "REGISTER_INFO_CONTENT",
        "LOGIN_FORM_TITLE",
        "LOGIN_FORM_DESCRIPTION",
        "REGISTER_NETWORK_PAGE_TITLE",
        "REGISTER_NETWORK_PAGE_DESCRIPTION",
        "EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_SUBJECT",
        "EMAIL_RECRUITER_ASK_EMPLOYEE_FOR_REFERRAL_BODY",
        "EMAIL_RECRUITER_INVITE_EMPLOYEE_TO_PLATFORM_SUBJECT",
        "EMAIL_RECRUITER_INVITE_EMPLOYEE_TO_PLATFORM_BODY",
      ]
    }) {
      key
      lang
      type
      default
      placeholder
      value
    }
  }
  `;

const SettingsQuery = gql`
  ${CompanyBrandingSettingsFragment}

  query SettingsPreferences {
    company {
      ...CompanyBrandingSettings
    }
  }
  `;

const SettingsMutation = gql`
  ${CompanyBrandingSettingsFragment}

  mutation SettingsPreferences($update: CompanyUpdateInput!) {
    updateCompany(update: $update) {
      ...CompanyBrandingSettings
    }
  }
  `;

export default {
  name: 'CompanyBrandingSettings',
  components: {
    FormBlock,
    ToggleBlock,
    LoadingSpinner,
  },
  data: () => ({
    company: null,
    err: null,
    formErrs: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
    formStates: Object.keys(BLOCKS).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {}),
  }),
  metaInfo: {
    title: 'Branding Settings',
  },
  apollo: {
    company: {
      query: SettingsQuery,
      error(err) { this.err = `${err}`; },
    },
  },
  computed: {
    blocks: () => BLOCKS,
  },
  methods: {
    getValues(key) {
      return this.blocks[key].fields.reduce((values, field) => {
        if (field.type === 'template') {
          const template = this.company.templates.filter(t => field.key.replace('template.', '') === t.key).shift();
          if (template) {
            values[field.key] = template;
          }
        } else if (field.type === 'colorPicker') {
          const branding = this.company.branding[field.key.replace('branding.', '')];
          if (branding) {
            values[field.key] = branding;
          } else if (field.defaultValue) {
            values[field.key] = field.defaultValue;
          } else {
            values[field.key] = '#FFFFFF';
          }
        } else {
          values[field.key] = _get(this.company, field.key, null);
        }
        return values;
      }, {});
    },
    async saveValues(key, values) {
      try {
        const block = this.blocks[key];
        if (!block) {
          throw new Error(`Unknown block ${key}`);
        }

        this.formStates[key] = 'SAVING';
        this.formErrs[key] = null;

        const update = block.fields.reduce((update, field) => {
          let value = values[field.key];
          if (field.type.startsWith('asset-') && value && value.id) {
            value = { id: value.id };
          }

          if (field.key.startsWith('branding.')) {
            update.branding = update.branding || {};
            if (field.key.startsWith('branding.dashboardCard')) {
              update.branding.dashboardCard = update.branding.dashboardCard || {};
              update.branding.dashboardCard[field.key.replace('branding.dashboardCard.', '')] = value;
              if (Object.values(update.branding.dashboardCard).reduce((r, v) => r && !v, true)) {
                update.branding.dashboardCard = null;
              }
            } else {
              update.branding[field.key.replace('branding.', '')] = value;
            }
          } else if (field.key.startsWith('preferences.')) {
            update.preferences = update.preferences || {};
            update.preferences[field.key.replace('preferences.', '')] = value;
          } else if (field.key.startsWith('template.')) {
            const { key, lang, value: v } = value;
            update.templates = update.templates || [];
            update.templates.push({ key, lang, value: v });
          } else {
            update[field.key] = value;
          }
          return update;
        }, {});

        // if (block.compileCSS && update.branding) {
        //   update.branding.compiledCSS = this.compileBranding(update);
        // }

        const result = await this.updateCompany(update);
        this.formStates[key] = result ? 'SAVED' : 'ERRORED';
      } catch (err) {
        this.$log.error(err);
        this.formStates[key] = 'ERRORED';
        this.formErrs[key] = `${err}`;
      }
    },
    async updateCompany(update) {
      try {
        this.$log.debug({ update });

        await this.$apollo.mutate({
          mutation: SettingsMutation,
          variables: { update },
          update(store, { data: { updateCompany: company } }) {
            const data = store.readQuery({ query: SettingsQuery });
            data.company = company;
            store.writeQuery({ query: SettingsQuery, data });
          },
        });

        return true;
      } catch (err) {
        this.$log.error(err);
        return false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/Branding.vue